import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidate, success } from 'services/auth';
import { camelcaseKeys } from 'utils/formatting';
import 'whatwg-fetch';
import { ENDPOINT } from '../../config/environment';
import { patch, post } from '../../services/fetch';
import { AuthSchema } from '../schemas';

export function useAuthWithEmail() {
  return useMutation(
    async ({ email, password }: { email: string; password: string }) => {
      const request: RequestInit = {
        headers: {
          accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ auth: { email, password } }),
        method: 'POST',
        credentials: 'include',
      };
      const response = await fetch(`${ENDPOINT}/auth/sign_in`, request);
      const data = camelcaseKeys(await response.json(), { deep: true });
      if (!response.ok) {
        throw data;
      }
      return data;
    },
    {
      onSuccess: data => {
        success(data);
      },
      onError: ({ errors }) => {
        invalidate();
        return errors;
      },
    }
  );
}
export const authWithToken = async (token: string) => {
  const request = {
    headers: {
      accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    method: 'POST',
  };
  const response = await fetch(`${ENDPOINT}/users/me`, request);
  const data = camelcaseKeys(await response.json(), { deep: true });
  if (!response.ok) {
    throw data;
  }
  return data as AuthSchema;
};
export function generateAuthQueryKey() {
  return ['users', 'me'];
}

export function useAuthExtendSession() {
  const queryClient = useQueryClient();

  return useMutation(authExtendSession, {
    onSuccess: data => {
      success(data);
      queryClient.setQueryData(generateAuthQueryKey(), data);
    },
  });
}
export function useAuthenticate({
  token,
  onSuccess,
  refetchOnMount = false,
  initialData,
  shouldInvalidate = true,
}: {
  refetchOnMount: boolean;
  initialData?: AuthSchema | null;
  onSuccess?: (data: AuthSchema) => void;
  shouldInvalidate?: boolean;
  token?: string | null;
}) {
  return useQuery(
    generateAuthQueryKey(),
    async () => {
      try {
        const headers: HeadersInit = {
          accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        const response = await fetch(`${ENDPOINT}/users/me`, {
          method: 'POST',
          credentials: 'include',
          headers,
        });
        const data: AuthSchema = camelcaseKeys(await response.json(), { deep: true });
        if (!response.ok) {
          throw data;
        }
        success(data);
        if (onSuccess) {
          onSuccess(data);
        }
        return data;
      } catch {
        if (shouldInvalidate) {
          invalidate();
        }
        return null;
      }
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      initialData,
      refetchOnMount,
    }
  );
}

export function useAuthPasswordless() {
  return useMutation(
    async (email: string) => {
      const response = await post('/passwordless/sign_in', { email, const: 'user' });
      return response;
    },
    {
      onError: ({ errors }) => {
        invalidate();
        return errors;
      },
    }
  );
}
export const requestForNewPassword = async (email: string) => {
  const payload = { email, const: 'user' };
  await patch('/auth/reset', payload);
};
export const setPassword = async (email: string, token: string, password: string) => {
  const payload = {
    email,
    token,
    password,
    passwordConfirmation: password,
    const: 'user',
  };
  const response = await post('/reset_password', payload);
  return response;
};
export const resetPassword = async (payload: {
  const: string;
  password: string;
  passwordConfirmation: string;
  token: string;
}) => {
  const response = await post(`/reset_password`, payload);
  return response;
};

interface UpdatePasswordPayload {
  oldPassword: string;
  password: string;
  passwordConfirmation: string;
}
export const updatePassword = async (
  id: number,
  updatePasswordPayload: UpdatePasswordPayload
): Promise<string> => {
  const payload = { user: updatePasswordPayload };
  const r = await post(`/users/${id}/update_password`, payload);
  return r.token;
};
export function useSignOut() {
  return useMutation(async () => {
    await post(`/auth/sign_out`);
  });
}
export async function authSwitchTeam(teamId: number | string) {
  const response = await post('/auth/switch_teams', { teamId });
  return response as AuthSchema;
}

export async function authExtendSession() {
  const authData = localStorage.getItem('auth/data');
  const token = authData ? (JSON.parse(authData) as AuthSchema)?.session?.jwt : undefined;

  const headers: HeadersInit = {
    accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(`${ENDPOINT}/auth/extend_session`, {
    method: 'POST',
    credentials: 'include',
    headers,
  });

  if (response.status === 404) {
    const fallbackHeaders: HeadersInit = {
      accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
    };

    const fallbackResponse = await fetch(`${ENDPOINT}/users/me`, {
      method: 'POST',
      credentials: 'include',
      headers: fallbackHeaders,
    });

    if (!fallbackResponse.ok) {
      throw new Error(`HTTP ${fallbackResponse.status}: ${fallbackResponse.statusText}`);
    }

    const data: AuthSchema = camelcaseKeys(await fallbackResponse.json(), { deep: true });
    return data;
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data: AuthSchema = camelcaseKeys(await response.json(), { deep: true });
  return data;
}
